import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash, Key, PencilSimple, FloppyDisk, X, EnvelopeSimple } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "cleaner", label: "Cleaner" },
  { value: "guest", label: "Guest" },
  { value: "renter", label: "Renter" },
];

const CATEGORY_STYLES = {
  manager: "bg-accent/15 text-accent border-accent/40",
  guest: "bg-foreground/10 text-foreground border-foreground/30",
  renter: "bg-secondary text-muted-foreground border-border",
  cleaner: "bg-secondary text-muted-foreground border-border",
};

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function GuestCodes() {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [endDate, setEndDate] = useState("");
  const [category, setCategory] = useState("renter");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/guest-codes");
      setCodes(data.codes || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Enter a code");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/guest-codes", { code: code.trim(), label: name.trim(), category, end_date: endDate });
      toast.success("Login added");
      setCode("");
      setName("");
      setEndDate("");
      setCategory("renter");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---- email guest their link + QR + personal code ----
  const [emailFor, setEmailFor] = useState(null);
  const [emailAddr, setEmailAddr] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const sendGuestEmail = async (id) => {
    if (!emailAddr.trim()) {
      toast.error("Enter the guest's email address");
      return;
    }
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/admin/guest-codes/${id}/email`, { to: emailAddr.trim() });
      if (data.emailed) {
        toast.success(`Guidebook link, QR and access code sent to ${emailAddr.trim()}`);
      } else {
        toast.info("Saved — the email service activates with the live site, so no email went out here.");
      }
      setEmailFor(null);
      setEmailAddr("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEmailBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/guest-codes/${id}`);
      toast.success("Code removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---- inline edit ----
  const [editingId, setEditingId] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editCategory, setEditCategory] = useState("guest");
  const [editSaving, setEditSaving] = useState(false);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditCode(c.code);
    setEditName(c.label || "");
    setEditEndDate(c.end_date || "");
    setEditCategory(c.category || "guest");
  };

  const saveEdit = async () => {
    if (!editCode.trim()) {
      toast.error("Code is required");
      return;
    }
    setEditSaving(true);
    try {
      await api.patch(`/admin/guest-codes/${editingId}`, { code: editCode.trim(), label: editName.trim(), category: editCategory, end_date: editEndDate });
      toast.success("Login updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="guest-codes-panel">
      <span className="subhead text-accent text-sm">Guidebook Access</span>
      <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Guidebook Logins</h2>
      <p className="text-muted-foreground mt-3 text-sm max-w-lg">
        These codes unlock the digital guidebook only — they are separate from the front door
        keypad code. Add an end date to have a login stop working automatically after that date;
        leave it blank for a login that never expires.
      </p>

      <form onSubmit={add} className="mt-8 border border-border p-5 bg-secondary/40 space-y-4" data-testid="add-code-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Smith family" className={inputCls} data-testid="new-code-name" />
          </div>
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">End Date (optional)</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} data-testid="new-code-end-date" />
          </div>
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="new-code-category" className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`category-option-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Code</label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. smith-mar" className={inputCls} data-testid="new-code-input" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="category-note">
          <span className="font-semibold text-foreground/80">Owner</span> — guidebook + booking calendar + departure checklist&nbsp;·&nbsp;
          <span className="font-semibold text-foreground/80">Manager</span> — guidebook + calendar (read-only)&nbsp;·&nbsp;
          <span className="font-semibold text-foreground/80">Cleaner</span> — guidebook only&nbsp;·&nbsp;
          <span className="font-semibold text-foreground/80">Guest</span> — guidebook + departure checklist&nbsp;·&nbsp;
          <span className="font-semibold text-foreground/80">Renter</span> — guidebook only
        </p>
        <button type="submit" disabled={saving} data-testid="add-code-btn" className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-5 pr-6 py-3 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60">
          <Plus size={16} weight="bold" /> {saving ? "Adding…" : "Add code"}
        </button>
      </form>

      <div className="mt-8 space-y-px bg-border border border-border" data-testid="codes-list">
        {loading ? (
          <p className="bg-background p-5 text-muted-foreground text-sm">Loading…</p>
        ) : (
          codes.map((c) => {
            const expired = !!(c.end_date && c.end_date < todayISO());
            return (
            <React.Fragment key={c.id}>
            <div className="bg-background px-5 py-4 flex items-center justify-between gap-4" data-testid={`code-row-${c.id}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <Key size={18} className="text-accent shrink-0" weight="duotone" />
                {editingId === c.id ? (
                  <div className="flex flex-wrap items-center gap-3 flex-1" data-testid={`edit-form-${c.id}`}>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Name"
                      className={inputCls + " w-40"}
                      data-testid={`edit-name-input-${c.id}`}
                    />
                    <Input
                      type="date"
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                      className={inputCls + " w-36"}
                      data-testid={`edit-end-date-input-${c.id}`}
                    />
                    <Select value={editCategory} onValueChange={setEditCategory}>
                      <SelectTrigger data-testid={`edit-category-${c.id}`} className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={editCode}
                      onChange={(e) => setEditCode(e.target.value)}
                      placeholder="Code"
                      className={inputCls + " w-32"}
                      data-testid={`edit-code-input-${c.id}`}
                    />
                    <button
                      onClick={saveEdit}
                      disabled={editSaving}
                      data-testid={`save-code-${c.id}`}
                      className="inline-flex items-center gap-1.5 bg-foreground text-primary-foreground px-4 py-2 rounded-full text-xs font-semibold hover:bg-accent transition-colors disabled:opacity-60"
                    >
                      <FloppyDisk size={13} weight="bold" /> {editSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      data-testid={`cancel-edit-${c.id}`}
                      className="inline-flex items-center gap-1.5 border border-border px-4 py-2 rounded-full text-xs hover:bg-secondary transition-colors"
                    >
                      <X size={13} /> Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 flex-1 min-w-0 lg:grid lg:grid-cols-[minmax(0,1fr)_8.5rem_5.5rem_7rem] lg:gap-x-5 text-left">
                    <span className="text-sm text-foreground font-medium truncate text-left" data-testid={`code-name-${c.id}`}>
                      {c.label || <span className="text-muted-foreground">—</span>}
                    </span>
                    <span className="text-sm text-muted-foreground whitespace-nowrap text-left" data-testid={`code-end-date-${c.id}`}>
                      {c.end_date ? `Ends ${fmtDate(c.end_date)}` : "No expiry"}
                      {expired && (
                        <span className="ml-2 inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-destructive/40 bg-destructive/10 text-destructive align-middle" data-testid={`expired-badge-${c.id}`}>
                          Expired
                        </span>
                      )}
                    </span>
                    <span className="text-left">
                      <span
                        data-testid={`category-badge-${c.id}`}
                        className={
                          "inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full border capitalize whitespace-nowrap " +
                          (CATEGORY_STYLES[c.category] || CATEGORY_STYLES.guest)
                        }
                      >
                        {c.category || "guest"}
                      </span>
                    </span>
                    <span className="font-display text-lg text-foreground tracking-wide whitespace-nowrap text-left" data-testid={`code-value-${c.id}`}>{c.code}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <button
                  onClick={() => {
                    setEmailFor(emailFor === c.id ? null : c.id);
                    setEmailAddr("");
                  }}
                  data-testid={`email-code-${c.id}`}
                  className="text-muted-foreground hover:text-accent transition-colors"
                  title="Email guest their link, QR & code"
                >
                  <EnvelopeSimple size={17} />
                </button>
                <button
                  onClick={() => startEdit(c)}
                  data-testid={`edit-code-${c.id}`}
                  className="text-muted-foreground hover:text-accent transition-colors"
                  title="Edit login"
                >
                  <PencilSimple size={17} />
                </button>
                <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`delete-code-${c.id}`} className="text-destructive/70 hover:text-destructive transition-colors">
                    <Trash size={18} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-popover">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove this code?</AlertDialogTitle>
                    <AlertDialogDescription>
                      &ldquo;{c.code}&rdquo;{c.label ? ` (${c.label})` : ""} will no longer unlock the guidebook.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(c.id)} data-testid={`confirm-delete-code-${c.id}`} className="bg-destructive hover:bg-destructive/90">
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </div>
            </div>
              {emailFor === c.id && (
                <div className="bg-secondary/50 p-4 flex flex-wrap items-center gap-3" data-testid={`email-form-${c.id}`}>
                  <span className="text-xs text-muted-foreground shrink-0">
                    Email guidebook link, QR &amp; code <span className="font-semibold text-foreground/80">{c.code}</span> to:
                  </span>
                  <Input
                    type="email"
                    value={emailAddr}
                    onChange={(e) => setEmailAddr(e.target.value)}
                    placeholder="guest@email.com"
                    className={inputCls + " flex-1 min-w-44"}
                    data-testid={`email-input-${c.id}`}
                  />
                  <button
                    onClick={() => sendGuestEmail(c.id)}
                    disabled={emailBusy}
                    data-testid={`send-email-${c.id}`}
                    className="inline-flex items-center gap-1.5 bg-foreground text-primary-foreground px-4 py-2 rounded-full text-xs font-semibold hover:bg-accent transition-colors disabled:opacity-60"
                  >
                    <EnvelopeSimple size={13} weight="bold" /> {emailBusy ? "Sending…" : "Send"}
                  </button>
                  <button
                    onClick={() => setEmailFor(null)}
                    data-testid={`cancel-email-${c.id}`}
                    className="inline-flex items-center gap-1.5 border border-border px-4 py-2 rounded-full text-xs hover:bg-secondary transition-colors"
                  >
                    <X size={13} /> Cancel
                  </button>
                </div>
              )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}
